import { useSelector } from 'react-redux';

import {
  Box,
  Card,
  FieldSet,
  Icon,
  LabeledControl,
  Paragraph,
  Radio,
  Select,
  Tag,
  ToggleCard,
} from '@zillow/constellation-10';
import { IconChevronRightOutline, IconWarningFilled } from '@zillow/constellation-icons';

import type { RootState } from '../../../store/types';
import { getFormattedPaymentMethod } from '@common/constants/PaymentMethod';
import type { RenterPaymentMethodInterface } from '@renter-hub/models/Types';
import { getVerifiedPaymentMethods } from '@common/paymentsUtils/multiplePaymentMethodsUtil';
import { VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';

interface Props {
  isLoading?: boolean;
  onOptionChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onPaymentMethodClick?: (paymentMethod: RenterPaymentMethodInterface) => void;
  selectedPaymentMethodId?: string;
  renderSelect?: boolean;
  renderRadioView?: boolean;
}

const AvailablePaymentMethods = ({
  isLoading,
  onOptionChange,
  onPaymentMethodClick,
  selectedPaymentMethodId,
  renderSelect,
  renderRadioView,
}: Props) => {
  const paymentMethods = useSelector((state: RootState) => state.payments.renter.dashboard.paymentMethods);

  const getDisplayPaymentMethods = () => {
    if (renderRadioView || renderSelect) {
      // For radio/select views, only show verified payment methods
      return getVerifiedPaymentMethods(paymentMethods);
    }

    return paymentMethods;
  };

  const displayPaymentMethods = getDisplayPaymentMethods();

  if (renderSelect) {
    return (
      <Box>
        <Paragraph textStyle="body-bold">Payment method</Paragraph>
        <Select value={selectedPaymentMethodId} onChange={onOptionChange} disabled={isLoading}>
          {displayPaymentMethods?.map((paymentMethod) => (
            <option key={paymentMethod.id} value={paymentMethod.id}>
              {`${paymentMethod.nickname || paymentMethod.displayName} ....${paymentMethod.accountLast4}`}
            </option>
          ))}
        </Select>
      </Box>
    );
  }

  const getSubText = (paymentMethod: RenterPaymentMethodInterface) => (
    <Box css={{ display: 'flex', flexDirection: 'column' }}>
      <Paragraph textStyle="body-lg-bold">{paymentMethod.nickname || paymentMethod.displayName}</Paragraph>
      <Paragraph css={{ color: 'text.subtle' }} textStyle="body-sm">
        {`${getFormattedPaymentMethod(paymentMethod.accountType)} ....${paymentMethod.accountLast4}`}
      </Paragraph>
    </Box>
  );

  const getTagText = (paymentMethod: RenterPaymentMethodInterface) => {
    if (paymentMethod.paymentMethodStatus === 'pendingAutomaticVerification') {
      return 'Verification In Progress';
    } else if (paymentMethod.paymentMethodStatus === 'pendingManualVerification') {
      return 'Verification needed';
    } else if (paymentMethod.paymentMethodStatus === VERIFICATION_STATUS.PLAID_REVOKED) {
      return 'Reconnection needed';
    }
    return null;
  };

  const WrapperComponent = renderRadioView ? ToggleCard : Card;
  return (
    <FieldSet aria-label="Available payment methods">
      {displayPaymentMethods?.map((paymentMethod) => (
        <WrapperComponent
          interactive={true}
          key={paymentMethod.id}
          elevated={false}
          selected={renderRadioView ? selectedPaymentMethodId === paymentMethod.id : undefined}
          css={{
            padding: 'default',
            ...(renderRadioView && { marginBottom: 'default' }),
          }}
          onClick={() => onPaymentMethodClick?.(paymentMethod)}
        >
          {renderRadioView ? (
            <LabeledControl
              label={getSubText(paymentMethod)}
              control={<Radio checked={selectedPaymentMethodId === paymentMethod.id} />}
            />
          ) : (
            <Box
              css={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'tighter',
                flexWrap: 'wrap',
                rowGap: 'tighter',
              }}
            >
              <Box css={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                {getSubText(paymentMethod)}
              </Box>
              <Box css={{ display: 'flex', alignItems: 'center', gap: 'default', flexShrink: 0 }}>
                {(paymentMethod.paymentMethodStatus === 'pendingAutomaticVerification' ||
                  paymentMethod.paymentMethodStatus === 'pendingManualVerification' ||
                  paymentMethod.paymentMethodStatus === VERIFICATION_STATUS.PLAID_REVOKED) && (
                  <Tag
                    icon={<IconWarningFilled />}
                    css={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}
                    size="md"
                    tone="warning"
                  >
                    {getTagText(paymentMethod)}
                  </Tag>
                )}
                <Icon size="md" css={{ color: 'brand' }} render={<IconChevronRightOutline />} />
              </Box>
            </Box>
          )}
        </WrapperComponent>
      ))}
    </FieldSet>
  );
};

export default AvailablePaymentMethods;
